import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../redux/store";
import TextInput from "../../components/TextInput/TextInput";
import Button from "../../components/Button/Button";
import Checkbox from "../../components/Checkbox/Checkbox";
import OtpComponent from "../../components/OtpComponent";
import HomePageHeader from "../Onboarding/HomePageHeader";
import AuthFooter from "../../components/AuthFooter/AuthFooter";
import { validateEmail, validatePassword } from "../../utils";
import { emailExists } from "../../redux/slices/CurrentUserSlice";
import { ToastContext } from "../../contexts/ToastContext/ToastContext";
import { ToastVariants } from "../../models";
import { useContext } from "react";
import { AuthContext } from "../../contexts/AuthContext/AuthContext";
import { logger } from "../../utils/logger";
import PasswordErrorList from "../../components/PasswordErrorList/PasswordErrorList";
import { officeSignUpV2 } from "../../redux/slices/OfficeSlice";
import { OfficeSignUpV2 } from "../../models/create-office.model";
import { PayloadAction } from "@reduxjs/toolkit";
import { Office } from "../../models/office.model";
import { resetOnboarding } from "../../redux/slices/OfficeOnboardingSlice";
import PipLogo from "../../assets/images/pip-logo.png";
import PhipaLogo from "../../assets/images/phipa-logo.png";
import CaLogo from "../../assets/images/CA.svg";
const log = logger.getLogger("PhysicianSignup");

const validationsInitData = [
  {
    id: "lowerCase",
    isValid: false,
    msg: "Password must contain a lower case letter",
  },
  {
    id: "upperCase",
    isValid: false,
    msg: "Password must contain an upper case letter",
  },
  {
    id: "specialCharacter",
    isValid: false,
    msg: "Password must contain a special character",
  },
  { id: "cotainNumber", isValid: false, msg: "Password must contain a number" },
  {
    id: "eightCharacters",
    isValid: false,
    msg: "Password must contain at least 8 characters",
  },
  { id: "noSpaces", isValid: false, msg: "Password cannot contain spaces" },
  { id: "passwordMatch", isValid: false, msg: "Password must match" },
];

export default function PhysicianSignupPage() {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const auth = useContext(AuthContext);
  const toast = useContext(ToastContext);
  const params = new URLSearchParams(window.location.search);
  const signEmail = params.get("email");

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [officeName, setOfficeName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [showError, setShowError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showVerification, setShowVerification] = useState(false);
  const [otpInput, setOtpInput] = useState("");
  const [pwdValidations, setPwdValidations] = useState(validationsInitData);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [createdOffice, setCreatedOffice] = useState<Office | null>(null);
  const [termsAccepted, setTermsAccepted] = useState(false);
  useEffect(() => {
    if (signEmail) setEmail(signEmail);
  }, [signEmail]);

  const handlePasswordChange = (value: string) => {
    const newValidations = pwdValidations.map(item => {
      const isValid = validatePassword(item.id, value);
      return { ...item, isValid };
    });
    setPwdValidations(newValidations);
    setPassword(value);
  };

  const handleConfirmPasswordChange = (value: string) => {
    const isValid = password === value;
    const validationErrors = [...pwdValidations];
    validationErrors[6] = { ...validationErrors[6], isValid };
    setPwdValidations(validationErrors);
    setConfirmPassword(value);
  };

  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    // First Name validation
    if (!firstName || !firstName.trim()) {
      newErrors.firstName = "First name is required.";
    } else {
      const trimmedFirstName = firstName.trim();
      if (trimmedFirstName.length < 2) {
        newErrors.firstName = "First name must be at least 2 characters.";
      } else if (trimmedFirstName.length > 25) {
        newErrors.firstName = "First name must be at most 25 characters.";
      } else if (!/^[a-zA-Z0-9\s'.-]+$/.test(trimmedFirstName)) {
        newErrors.firstName = "First name must contain only letters.";
      }
    }

    // Last Name validation
    if (!lastName || !lastName.trim()) {
      newErrors.lastName = "Last name is required.";
    } else {
      const trimmedLastName = lastName.trim();
      if (trimmedLastName.length < 2) {
        newErrors.lastName = "Last name must be at least 2 characters.";
      } else if (trimmedLastName.length > 25) {
        newErrors.lastName = "Last name must be at most 25 characters.";
      } else if (!/^[a-zA-Z0-9\s'.-]+$/.test(trimmedLastName)) {
        newErrors.lastName = "Last name must contain only letters.";
      }
    }

    // Office Name validation
    if (!officeName || !officeName.trim()) {
      newErrors.officeName = "Office name is required.";
    } else {
      const trimmedOfficeName = officeName.trim();
      if (trimmedOfficeName.length < 2) {
        newErrors.officeName = "Office name must be at least 2 characters.";
      } else if (trimmedOfficeName.length > 50) {
        newErrors.officeName = "Office name must be at most 50 characters.";
      } else if (!/^[a-zA-Z0-9\s.,&()'@-]+$/.test(trimmedOfficeName)) {
        newErrors.officeName = "Office name contains invalid characters.";
      }
    }

    // Email validation
    if (!email || !email.trim()) {
      newErrors.email = "Email is required.";
    } else {
      const trimmedEmail = email.trim();
      // Check for spaces in email
      if (/\s/.test(trimmedEmail)) {
        newErrors.email = "Please enter a valid email address.";
      } else if (!validateEmail(trimmedEmail)) {
        newErrors.email = "Please enter a valid email address.";
      }
    }

    // Password validation
    if (!password) {
      newErrors.password = "Password is required.";
    } else if (password.length < 8) {
      newErrors.password = "Password must be at least 8 characters long.";
    } else if (!pwdValidations.every(v => v.isValid)) {
      newErrors.password = "Password must include uppercase, lowercase, number, and a special character.";
    }

    // Confirm Password validation
    if (!confirmPassword) {
      newErrors.confirmPassword = "Please confirm your password.";
    } else if (password !== confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match.";
    }

    // Terms acceptance validation
    if (!termsAccepted) {
      newErrors.termsAccepted = "You must accept the Terms of Use and Privacy Policy to continue.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    setShowError(true);
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      // Check if email exists
      const emailExist = await dispatch(emailExists({ email: email.trim() }));
      if (emailExist.payload) {
        setErrors({ email: "Email already exists" });
        setLoading(false);
        return;
      }

      // Create office and user account
      const body: OfficeSignUpV2 = {
        email: email.trim(),
        officeName: officeName.trim(),
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        password: password,
      };

      const res = (await dispatch(officeSignUpV2({ body }))) as PayloadAction<Office | undefined>;

      if (res.payload?.officeId) {
        // Store the office data to access step later
        setCreatedOffice(res.payload);
        // Office created successfully, now login and trigger email verification
        await loginAndVerify(res.payload);
      } else {
        toast?.openToast("Something went wrong during signup. Please try again.", 2000, ToastVariants.Error);
        setLoading(false);
      }
    } catch (error) {
      log.error(`Signup error: ${JSON.stringify(error)}`);
      toast?.openToast("Something went wrong. Please try again.", 2000, ToastVariants.Error);
      setLoading(false);
    }
  };

  const loginAndVerify = async (office: Office) => {
    try {
      const res = await auth?.signInNew({ email: email.trim(), password: password });
      if (res?.emailVerificationRequired) {
        setShowVerification(true);
        setLoading(false);
      } else if (res) {
        // Login successful without verification needed
        toast?.openToast("Account created successfully!", 2000, ToastVariants.Success);
        // Reset any old onboarding data and start fresh
        dispatch(resetOnboarding());
        localStorage.setItem("showWelcomeModal", "true");
        navigate("/");
      } else {
        toast?.openToast("Account created but login failed. Please try logging in.", 2000, ToastVariants.Error);
        setLoading(false);
      }
    } catch (error) {
      log.error(`Login error: ${JSON.stringify(error)}`);
      toast?.openToast("Account created but login failed. Please try logging in.", 2000, ToastVariants.Error);
      setLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    setLoading(true);
    try {
      const res = await auth?.authenticateMFA(email.trim(), otpInput);
      if (res) {
        setShowVerification(false);
        setOtpInput("");
        toast?.openToast("Account created successfully! Welcome to Docnote.", 2000, ToastVariants.Success);
        // Reset any old onboarding data and start fresh
        dispatch(resetOnboarding());
        localStorage.setItem("showWelcomeModal", "true");
        navigate("/");
      } else {
        setErrors({ otp: "Invalid verification code" });
      }
    } catch (error) {
      log.error(`Verification error: ${JSON.stringify(error)}`);
      setErrors({ otp: "Verification failed. Please try again." });
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    try {
      const res = await auth?.signInNew({ email: email.trim(), password: password });
      if (res?.emailVerificationRequired) {
        toast?.openToast("A new code has been sent to your email.", 2000, ToastVariants.Success);
      }
    } catch (error) {
      log.error(`Resend code error: ${JSON.stringify(error)}`);
      toast?.openToast("Failed to resend code. Please try again.", 2000, ToastVariants.Error);
    }
  };

  return (
    <div className="auth-background">
      <HomePageHeader />
      <div className="relative w-full flex flex-grow justify-center overflow-hidden md:mt-16">
        <div className="relative z-10 flex px-4 md:w-[84%] max-w-[1600px] mx-auto gap-10">
          {/* Left Section - Marketing Content */}
          <div className="hidden lg:flex lg:w-3/5 flex-col justify-center py-11">
            <h1 className="text-5xl font-bold mb-5 text-[#ddee59]">Uninsured Services Made Easy</h1>
            <p className="text-[16px] text-white mb-8 leading-[1.8] font-inter max-w-[490px]">
              Whether you like to be completely hands on, you prefer to delegate the work to your team, or you do a little bit of both - Docnote is
              customizable to your office workflow. We help you with payment, document delivery, and patient transparency. The all-in-one solution for
              your uninsured needs.
            </p>
            <div className="text-white text-[18px] font-bold mb-3">Get started for free on the only platform that:</div>
            <div className="space-y-2 mb-8 font-inter">
              <div className="flex items-start space-x-2">
                <span className="text-green-400 text-[16px]">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M6 12.5L10 16.5L18 8.5" stroke="#ddee59" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </span>
                <p className="text-white">Automates your administrative work</p>
              </div>
              <div className="flex items-start space-x-2">
                <span className="text-green-400 text-[16px]">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M6 12.5L10 16.5L18 8.5" stroke="#ddee59" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </span>
                <p className="text-white">Guarantees payment for the work you do</p>
              </div>
              <div className="flex items-start space-x-2">
                <span className="text-green-400 text-[16px]">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M6 12.5L10 16.5L18 8.5" stroke="#ddee59" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </span>{" "}
                <p className="text-white">Works side-by-side with your EMR and AI Scribe</p>
              </div>
              <div className="flex items-start space-x-2 hidden">
                <span className="text-green-400 text-[16px]">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M6 12.5L10 16.5L18 8.5" stroke="#ddee59" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </span>{" "}
                <p className="text-white">Works with your AI scribe to complete the encounter</p>
              </div>
              <div className="flex items-start space-x-2 hidden">
                <span className="text-green-400 text-[16px]">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M6 12.5L10 16.5L18 8.5" stroke="#ddee59" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </span>{" "}
                <p className="text-white">Gives you full control</p>
              </div>
              <div className="flex items-start space-x-2 hidden">
                <span className="text-green-400 text-[16px]">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M6 12.5L10 16.5L18 8.5" stroke="#ddee59" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </span>{" "}
                <p className="text-white">Patients love</p>
              </div>
            </div>

            {/* Trust Section */}
            <div className="mb-8 max-w-[500px]">
              {/* Title */}
              <h2 className="text-white text-lg font-bold tracking-wider uppercase mb-6 text-center">
                TRUSTED BY CLINICS ACROSS{" "}
                <span className="inline-block mx-1">
                  <img src={CaLogo} alt="CANADA LOGO" />
                </span>{" "}
                CANADA
              </h2>

              {/* Logos Section */}
              <div className="flex items-center justify-center gap-8 mb-7">
                {/* PIP Logo */}
                <div className="flex-shrink-0">
                  <img src={PipLogo} alt="Personal Information Protection and Electronic Documents" className="h-[100px] w-auto" />
                </div>

                {/* PHIPA Logo */}
                <div className="flex-shrink-0">
                  <img src={PhipaLogo} alt="PHIPA Compliant" className="h-[52px] w-auto" />
                </div>
              </div>

              {/* Trust Badges */}
              <div className="flex flex-wrap justify-center gap-2">
                {/* Encryption Badge */}
                <div className="bg-[#4A5568] rounded-lg p-3 flex items-center gap-2">
                  <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span className="text-white text-xs font-medium">Encryption in transit</span>
                </div>

                {/* Canada Hosting Badge */}
                <div className="bg-[#4A5568] rounded-lg p-3 flex items-center gap-2">
                  <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M3 5a2 2 0 012-2h10a2 2 0 012 2v2a2 2 0 01-2 2H5a2 2 0 01-2-2V5zm2 0v2h10V5H5zm-2 6a2 2 0 012-2h10a2 2 0 012 2v2a2 2 0 01-2 2H5a2 2 0 01-2-2v-2zm2 0v2h10v-2H5z" />
                  </svg>
                  <span className="text-white text-xs font-medium">Canada-based hosting</span>
                </div>

                {/* PCI-DSS Badge */}
                <div className="bg-[#4A5568] rounded-lg p-3 flex items-center gap-2">
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="text-white text-xs font-medium">PCI-DSS (payments)</span>
                </div>

                {/* Designed for Canadian Badge */}
                <div className="bg-[#4A5568] rounded-lg p-3">
                  <span className="text-white text-xs font-medium">Designed for Canadian Physicians & Teams</span>
                </div>
              </div>
            </div>
          </div>

          {/* Right Section - Signup Form */}
          <div className="w-full w-[44%] max-w-[678px] flex items-center justify-center lg:py-11">
            <div className="w-full bg-[#F9F7F4] rounded-[20px] py-8 px-4 md:py-7 md:px-12">
              {!showVerification && (
                <>
                  <h2 className="text-[32px] font-bold mb-1.5">Create your Docnote account</h2>
                  <p className="font-inter text-[#1E294F]">
                    No subscription or contract required. Built for Canadian medical practitioners and their teams.
                  </p>

                  <Link
                    to="/patient"
                    className="inline-block text-lg my-5 px-6 py-3.5 rounded-[30px] bg-[#E5EFF8] font-bold border-2 border-[#3B82F6] shadow-md hover:shadow-lg transition-shadow whitespace-nowrap"
                  >
                    Are you a patient? <span className="text-link-blue text-lg">Sign up here →</span>
                  </Link>
                </>
              )}

              {!showVerification ? (
                <form
                  className="space-y-4"
                  onSubmit={e => {
                    e.preventDefault();
                    handleSubmit();
                  }}
                >
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <div>
                      <TextInput
                        label="First Name"
                        placeholder="First Name"
                        value={firstName}
                        onChangeFunc={e => {
                          setFirstName(e.target.value);
                          if (errors.firstName) setErrors({ ...errors, firstName: "" });
                        }}
                        isRequired
                        isError={!!errors.firstName}
                        errorMsg={errors.firstName}
                        name="firstName"
                        extraInputClass="bg-transparent"
                      />
                    </div>

                    <div>
                      <TextInput
                        label="Last Name"
                        placeholder="Last Name"
                        value={lastName}
                        onChangeFunc={e => {
                          setLastName(e.target.value);
                          if (errors.lastName) setErrors({ ...errors, lastName: "" });
                        }}
                        isRequired
                        isError={!!errors.lastName}
                        errorMsg={errors.lastName}
                        name="lastName"
                        extraInputClass="bg-transparent"
                      />
                    </div>
                  </div>

                  <div>
                    <TextInput
                      label="Office Name"
                      placeholder="Office Name"
                      value={officeName}
                      onChangeFunc={e => {
                        setOfficeName(e.target.value);
                        if (errors.officeName) setErrors({ ...errors, officeName: "" });
                      }}
                      isRequired
                      isError={!!errors.officeName}
                      errorMsg={errors.officeName}
                      name="officeName"
                      extraInputClass="bg-transparent"
                    />
                  </div>

                  <div>
                    <TextInput
                      label="Email"
                      placeholder="email@example.com"
                      value={email}
                      onChangeFunc={e => {
                        setEmail(e.target.value);
                        if (errors.email) setErrors({ ...errors, email: "" });
                      }}
                      isRequired
                      isError={!!errors.email}
                      errorMsg={errors.email}
                      name="email"
                      extraInputClass="bg-transparent"
                    />
                  </div>

                  <div>
                    <TextInput
                      label="Password"
                      placeholder="••••••••••"
                      value={password}
                      onChangeFunc={e => handlePasswordChange(e.target.value)}
                      isPassword
                      isRequired
                      isError={!!errors.password}
                      errorMsg={errors.password}
                      name="password"
                      extraInputClass="bg-transparent placeholder:text-navy-blue placeholder:opacity-50"
                    />
                    {password && (
                      <div className="mt-2 space-y-1">
                        {pwdValidations.map((validation, index) => (
                          <PasswordErrorList key={index} isValid={validation.isValid} message={validation.msg} />
                        ))}
                      </div>
                    )}
                  </div>

                  <div>
                    <TextInput
                      label="Confirm Password"
                      placeholder="••••••••••"
                      value={confirmPassword}
                      onChangeFunc={e => handleConfirmPasswordChange(e.target.value)}
                      isPassword
                      isRequired
                      isError={!!errors.confirmPassword}
                      errorMsg={errors.confirmPassword}
                      name="confirmPassword"
                      extraInputClass="bg-transparent placeholder:text-navy-blue placeholder:opacity-50"
                    />
                  </div>

                  <div>
                    <div className="flex items-start gap-2">
                      <div className="mt-1">
                        <Checkbox
                          checked={termsAccepted}
                          onChange={e => {
                            setTermsAccepted(e.target.checked);
                            if (errors.termsAccepted) setErrors({ ...errors, termsAccepted: "" });
                          }}
                          name="termsAccepted"
                          id="termsAccepted"
                          required
                        />
                      </div>
                      <div className="font-inter font-medium">
                        By signing up, I agree to Docnote&apos;s{" "}
                        <Link to="/terms-of-use" className="text-link-blue" target="_blank" rel="noopener noreferrer">
                          Terms of Use
                        </Link>{" "}
                        &{" "}
                        <Link to="/privacy-policy" className="text-link-blue" target="_blank" rel="noopener noreferrer">
                          Privacy Policy
                        </Link>
                        .
                      </div>
                    </div>
                    {errors.termsAccepted && <p className="text-[#ee1818] text-sm mt-1">{errors.termsAccepted}</p>}
                  </div>

                  <Button
                    text="Sign Up"
                    onClick={() => {}}
                    disabled={loading || !termsAccepted}
                    variant={loading ? "loading" : "Primary"}
                    width="full"
                    className="mt-1 h-[50px] !text-[16px]"
                  />

                  <p className="text-center font-inter font-medium mt-5">
                    Already have an account?{" "}
                    <Link to="/login" className="text-link-blue">
                      Login
                    </Link>
                  </p>
                </form>
              ) : (
                <div>
                  <OtpComponent
                    value={email}
                    otpInput={otpInput}
                    setOtpInput={setOtpInput}
                    loading={loading}
                    showError={!!errors.otp}
                    submit={handleVerifyCode}
                    resend={handleResendCode}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {/* <div className="mt-10 bg-[#111C3D] border-t border-white/20 ">
        <AuthFooter extraClasses={"footerContainer mx-auto"} />
      </div> */}
    </div>
  );
}
